import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { SEARCH_RESULT_TYPE } from "common/constants";
import { formatCoordinates } from "common/utils/coordinates";
import { useIsScreenSize } from "../../../hooks/useWindowSize";
import { SearchContext } from "../../Search/SearchContext";
import { SearchResultsLayer } from "../layers/searchResults";
import {
  EventFeature,
  findRelevantFeature,
  LayerConfig,
  LayerHook,
  LayerHookBuilder,
  LayerId,
} from ".";
import { GeometryRef } from "./customMapLayer";
import { Feature } from "./types";
import { getPointFromFeature } from "./utils";

export type SearchResultsLayerConfig = LayerConfig;
const SEARCH_RESULTS_LAYER_ID = "search-results";
const SEARCH_RESULTS_SOURCE_ID = "search-results-source";
const interactiveLayerIds: LayerId[] = [
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PROPERTY}`,
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}`,
  `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}-fill`,
];

const getFeatureDisplay = (feature: EventFeature | Feature) => {
  if (feature.properties?.address) {
    return feature.properties.address as string;
  } else if (
    feature.sourceLayer === SEARCH_RESULT_TYPE.PARCEL &&
    feature.properties?.parcelNumber
  ) {
    return feature.properties.parcelNumber as string;
  } else {
    const point = getPointFromFeature(feature);
    if (point) {
      return formatCoordinates(point);
    } else return "";
  }
};

const useSearchResultsHook: LayerHookBuilder<SearchResultsLayerConfig> = ({
  config,
  helpers,
}) => {
  const hoveredSearchResult = useRef<Maybe<GeometryRef>>(null);
  const { searchResultsState, searchResultsDispatch, handleSearchResultClick } =
    useContext(SearchContext);
  const isDesktop = useIsScreenSize("desktop");

  const updateHoverStates = useCallback(
    (featureId: string, featureType: SEARCH_RESULT_TYPE) => {
      helpers.setFeatureState({
        id: featureId,
        field: "hover",
        source: SEARCH_RESULTS_SOURCE_ID,
        sourceLayer: featureType,
      });
      hoveredSearchResult.current = {
        id: featureId,
        source: SEARCH_RESULTS_SOURCE_ID,
        sourceLayer: featureType,
      };
    },
    [helpers],
  );

  useEffect(() => {
    if (hoveredSearchResult.current) {
      helpers.emit({
        type: "removeFeatureState",
        data: { field: "hover", ...hoveredSearchResult.current },
      });
    }
    if (searchResultsState.hoveredSearchResult) {
      const {
        feature: { id: hoveredFeatureId, featureType },
        hoverSource,
      } = searchResultsState.hoveredSearchResult;
      updateHoverStates(hoveredFeatureId, featureType);
      const hoveredFeature = helpers
        .featuresByFilter({
          layer:
            featureType === SEARCH_RESULT_TYPE.PROPERTY ?
              `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PROPERTY}`
            : `${SEARCH_RESULTS_LAYER_ID}-${SEARCH_RESULT_TYPE.PARCEL}-fill`,
          filter: ["to-boolean", true],
        })
        .find(feature => feature.id === hoveredFeatureId);
      if (hoveredFeature && hoverSource === "results") {
        const pixelCoordinates = helpers.getCoordinatesForFeature({
          feature: hoveredFeature,
        });
        if (pixelCoordinates) {
          helpers.emit({
            type: "setTooltip",
            data: {
              text: getFeatureDisplay(hoveredFeature),
              x: pixelCoordinates.x,
              y: pixelCoordinates.y,
            },
          });
        }
      }
    } else {
      helpers.emit({
        type: "removeTooltip",
        data: {},
      });
    }
  }, [searchResultsState.hoveredSearchResult, helpers, updateHoverStates]);

  return useMemo<LayerHook>(() => {
    return {
      // -3 so that the markers show above the click object menu
      zIndex: -3,
      interactiveLayerIds,
      render: () => {
        if (!searchResultsState.results?.length || !isDesktop) {
          return null;
        }
        return <SearchResultsLayer key={SEARCH_RESULTS_LAYER_ID} />;
      },
      canHandleClick: () => !!config.interactive?.click,
      canHandleHover: () => !!config.interactive?.hover,
      onClick: ({ event }) => {
        const feature = findRelevantFeature({
          event,
          layerId: interactiveLayerIds,
          getSortKey: feature =>
            feature.sourceLayer === SEARCH_RESULT_TYPE.PROPERTY ? 0 : 1,
        });
        if (feature) {
          helpers.emit({
            type: "removeTooltip",
            data: {},
          });
          hoveredSearchResult.current = null;

          const searchResult = searchResultsState.results?.find(
            result =>
              result.id === feature.id &&
              result.__typename === feature.sourceLayer,
          );
          if (searchResult) {
            handleSearchResultClick?.(searchResult);
          }
        }
        return !!feature;
      },
      onHover: ({ event }) => {
        if (hoveredSearchResult.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredSearchResult.current },
          });
          hoveredSearchResult.current = null;
        }
        const feature = findRelevantFeature({
          event,
          layerId: interactiveLayerIds,
          getSortKey: feature =>
            feature.sourceLayer === SEARCH_RESULT_TYPE.PROPERTY ? 0 : 1,
        });
        if (feature) {
          const featureType = feature.sourceLayer as SEARCH_RESULT_TYPE;
          updateHoverStates(feature.id, featureType);

          helpers.setCursor("pointer");
          helpers.emit({
            type: "setTooltip",
            data: {
              x: event.point.x,
              y: event.point.y,
              text: getFeatureDisplay(feature),
            },
          });

          searchResultsDispatch({
            type: "setHoveredSearchResult",
            feature: {
              id: feature.id,
              featureType,
            },
            hoverSource: "map",
          });
        } else {
          searchResultsDispatch({
            type: "clearHoveredSearchResult",
          });
        }
        return !!feature;
      },
      onFocusLost: () => {
        if (hoveredSearchResult.current) {
          helpers.emit({
            type: "removeFeatureState",
            data: { field: "hover", ...hoveredSearchResult.current },
          });
          hoveredSearchResult.current = null;
        }
        searchResultsDispatch({
          type: "clearHoveredSearchResult",
        });
      },
    };
  }, [
    config,
    helpers,
    searchResultsDispatch,
    updateHoverStates,
    handleSearchResultClick,
    searchResultsState.results,
    isDesktop,
  ]);
};

export default useSearchResultsHook;
